import React, { useEffect, useState, useRef } from 'react';
import API from '../../../utils/API'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import constants from '../../../utils/constants'
import { FormattedMessage } from 'react-intl';
import Toast from '../../../components/Toast/Toast';
import CopyIcon from '../../../assets/icons/common/copy.svg';
import EyeIcon from '../../../assets/icons/common/eye.svg';
import EyeOffIcon from '../../../assets/icons/common/eye-off.svg';
import './Configuration.css'

const Configuration = (props) => {
  const navigate = useNavigate();
  const { user } = props;
  const permissionIds = user.permissions.map((permission)=>{
    return permission.id
  });

  let [apiKey, setApiKey] = useState('');
  let [success, setSuccess] = useState('');
  let [error, setError] = useState('');
  let [showApiKey, setShowApiKey] = useState(false);
  let [singlewireToken, setSinglewireToken] = useState('');
  let [showToken, setShowToken] = useState(false);
  let [showTokenInput, setShowTokenInput] = useState(false);
  let [tokenInput, setTokenInput] = useState('');

  useEffect(() => {
    if(!permissionIds.includes(constants.permissionIds.aPlusEdit)){
      navigate('/forbidden');
    };
  }, [navigate, permissionIds]);

  // Remove unused function since we're using implicit flow (token, not code)
  // const exchangeCodeForToken - removed

  useEffect(() => {
    getApiKey();
    // Get stored Singlewire token on component mount
    API.getSinglewireToken().then((response) => {
      if(response.data.token){
        setSinglewireToken(response.data.token);
      }
    }).catch((error) => {
      console.error('Error getting Singlewire token:', error);
    });
  }, [])

  const getApiKey = () => {
    // Use Singlewire-specific API key method
    API.getSinglewireApiKey(user.account.id).then((response) => {
      if(response.data.success){
        setApiKey(response.data.apiKey);
      }
    }).catch((error) => {
      console.error('Error getting Singlewire API key:', error);
    });
  }

  const getSinglewireToken = () => {
    API.getSinglewireToken().then((response) => {
      if(response.data.token){
        setSinglewireToken(response.data.token);
      }
    }).catch((error) => {
      console.error('Error getting Singlewire token:', error);
    });
  }
  
  const handleManualTokenSubmit = async () => {
    if (!tokenInput.trim()) {
      setError(<FormattedMessage id="singlewireEmptyToken" defaultMessage="Please enter a token" />);
      return;
    }

    try {
      // Store the token directly without verification
      const storeResponse = await API.storeSinglewireToken(tokenInput.trim());
      if (storeResponse.data.success) {
        console.log('storeResponse.data.success:', storeResponse.data.success);
        setSinglewireToken(tokenInput.trim());
        setSuccess(<FormattedMessage id="singlewireTokenSuccess" defaultMessage="Successfully stored Singlewire token" />);
        setShowTokenInput(false);
        setTokenInput('');
        // Refresh to get the stored token
        setTimeout(() => getSinglewireToken(), 1000);
      }
    } catch (error) {
      console.error('Error storing token:', error);
      setError(<FormattedMessage id="singlewireTokenError" defaultMessage="Failed to store token. Please try again." />);
    }
  }

  const generateApiKey = () => {
    API.generateSinglewireApiKey(user.email, user.id).then((response) => {
      console.log('response:', response);
      if(response.data.success){
        setApiKey(response.data.apiKey);
        setSuccess(<FormattedMessage id="generateApiKeySuccess" defaultMessage="Successfully generated an API Key" />)
      }
    }).catch((error) => {
      console.error('Error generating API key:', error);
      setError(<FormattedMessage id="generateApiKeyError" defaultMessage="Error generating API key" />)
    });
  }

  const copyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
    setSuccess(<FormattedMessage id="apiKeyCopied" defaultMessage="API Key copied to clipboard" />)
  }

  const handleDisconnect = async () => {
    try {
      await API.disconnectSinglewire();
      setSinglewireToken('');
      setSuccess(<FormattedMessage 
        id="SINGLEWIRE_DISCONNECTED" 
        defaultMessage="Disconnected from Singlewire successfully" 
      />);
    } catch (error) {
      setError(<FormattedMessage 
        id="SINGLEWIRE_DISCONNECT_ERROR" 
        defaultMessage="Failed to disconnect from Singlewire" 
      />);
      console.error('Error disconnecting from Singlewire:', error);
    }
  }

  return (
    <>
      <div className="title"><FormattedMessage id="CONFIGURATION" defaultMessage="CONFIGURATION" /></div>
      <div className="cardContainer">
        <div className="custom-card-header">
          <FormattedMessage id="SB_CLOUD_API_KEY" defaultMessage="SB Cloud API Key" />
        </div>
        <div className="blueWhiteButton mb-3" id="generateApiKeyBtn" onClick={generateApiKey}>
          <FormattedMessage id="GENERATE_API_KEY" defaultMessage="Generate API Key" />
        </div>
        <div className="grayInputField d-flex justify-content-between align-items-center" >
          <div className={`text-start ${apiKey ? 'api-key-display' : 'api-key-placeholder'}`}>
            {apiKey ? (showApiKey ? apiKey : '•'.repeat(Math.min(apiKey.length, 40))) : (
              <FormattedMessage id="NO_API_KEY" defaultMessage="No API Key" />
            )}
          </div>
          <div className="button-group">
            <button 
              className="copy-button" 
              onClick={() => setShowApiKey(!showApiKey)}
              disabled={!apiKey}
            >
              {showApiKey ? (
                <img src={EyeOffIcon} alt={<FormattedMessage id="HIDE" defaultMessage="Hide" />} />
              ) : (
                <img src={EyeIcon} alt={<FormattedMessage id="SHOW" defaultMessage="Show" />} />
              )}
            </button>
            <button 
              className="copy-button" 
              onClick={copyApiKey}
              disabled={!apiKey}
            >
              <img src={CopyIcon} alt={<FormattedMessage id="COPY" defaultMessage="Copy" />} />
            </button>
          </div>
        </div>
        <p className="row text-muted mt-4 mb-2">
          <FormattedMessage 
            id="API_KEY_DOCS_REFERENCE" 
            defaultMessage="Please refer to your documentation on how to Configure API access" 
          />
        </p>
      </div>
      <div className="cardContainer mt-4">
        <div className="custom-card-header">
          <FormattedMessage id="SINGLEWIRE_AUTHENTICATION" defaultMessage="Singlewire Authentication" />
        </div>
        {!singlewireToken ? (
          <>
            <div className="grayInputField d-flex justify-content-between align-items-center">
              <div className="flex-grow-1">
                <input
                  type="text"
                  className="form-control border-0 bg-transparent p-0"
                  placeholder="Enter Singlewire API token here"
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleManualTokenSubmit()}
                />
              </div>
              <button
                className="blueWhiteButton ms-2"
                id="saveSinglewireTokenBtn"
                onClick={handleManualTokenSubmit}
                disabled={!tokenInput.trim()}
              >
                <FormattedMessage id="SAVE" defaultMessage="Save" />
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="grayInputField d-flex justify-content-between align-items-center mb-3">
              <div className={`text-start ${singlewireToken ? 'api-key-display' : 'api-key-placeholder'}`}>
                {showToken ? singlewireToken : '•'.repeat(Math.min(singlewireToken.length, 40))}
              </div>
              <div className="button-group">
                <button 
                  className="copy-button" 
                  onClick={() => setShowToken(!showToken)}
                >
                  {showToken ? (
                    <img src={EyeOffIcon} alt={<FormattedMessage id="HIDE" defaultMessage="Hide" />} />
                  ) : (
                    <img src={EyeIcon} alt={<FormattedMessage id="SHOW" defaultMessage="Show" />} />
                  )}
                </button>
              </div>
            </div>
            <div className="disconnectBtn" id="disconnectSinglewireBtn">
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Are you sure you want to disconnect from Singlewire?')) {
                    handleDisconnect();
                  }
                }}
                aria-label="Disconnect from Singlewire"
              >
                <FormattedMessage id="DISCONNECT_FROM_SINGLEWIRE" defaultMessage="Disconnect from Singlewire" />
              </button>
              <span className="warning-text">
                <FormattedMessage
                  id="SINGLEWIRE_DISCONNECT_WARNING"
                  defaultMessage="This will remove the stored Singlewire token. You can re-connect later."
                />
              </span>
            </div>
          </>
        )}
        {singlewireToken && (
          <p className="row text-muted mt-4 mb-2">
            <FormattedMessage 
              id="SINGLEWIRE_TOKEN_EXPIRATION" 
              defaultMessage="IMPORTANT: This token will expire 1 year from creation. Please renew token on your service account before it expires." 
            />
          </p>
        )}
      </div>
      <Toast 
        success={success}
        error={error}
        setSuccess={setSuccess}
        setError={setError}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.user
})

export default connect(mapStateToProps)(Configuration);